import { useState } from 'react';
import { Home, Building2, FileText, LogOut, User, Settings, Shield, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

export const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/properties', icon: Building2, label: 'Inmuebles' },
    { path: '/reports', icon: FileText, label: 'Informes' },
    { path: '/settings', icon: Settings, label: 'Configuración' },
  ];

  const handleLogout = () => {
    logout();
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-background">
      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-3 border-b bg-card shadow-sm">
        <img 
          src="https://customer-assets.emergentagent.com/job_propmanager-46/artifacts/6obc5v0l_ES2%20Logo%20Factura.jpg" 
          alt="ES2"
          className="h-10 w-auto rounded"
        />
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 top-[57px] bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Slide Menu */}
      <aside className={`
        md:hidden fixed right-0 top-[57px] bottom-0 w-64 bg-card shadow-lg z-50
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t bg-card">
          <div className="flex items-center gap-2 px-2 py-2 mb-2">
            {isAdmin() ? (
              <Shield size={16} className="text-amber-600" />
            ) : (
              <User size={16} className="text-muted-foreground" />
            )}
            <div className="flex flex-col">
              <span className="text-sm text-foreground font-medium">{user?.username}</span>
              {isAdmin() && (
                <span className="text-xs text-amber-600">Administrador</span>
              )}
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => { closeMobileMenu(); handleLogout(); }}
            data-testid="mobile-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-52 border-r bg-card shadow-sm flex-col">
        <div className="p-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_4a0404f9-cd85-432c-b1f6-9d1203fe859d/artifacts/70n05t0q_Logo%20ES2%20Apps.png" 
            alt="ES2 Espacios Singulares"
            className="w-full h-auto rounded"
          />
        </div>
        <nav className="px-3 space-y-1 flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
                }`}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t">
          <div className="flex items-center gap-2 px-2 py-1 mb-2">
            {isAdmin() ? (
              <Shield size={14} className="text-amber-600" />
            ) : (
              <User size={14} className="text-muted-foreground" />
            )}
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground truncate">{user?.username}</span>
              {isAdmin() && (
                <span className="text-[10px] text-amber-600 font-medium">Administrador</span>
              )}
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-xs"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={14} className="mr-1" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
};